import { useEffect, useMemo } from 'react';
import { useAuth } from 'src/auth';
import {
  useCreateApplicationGroupMutation,
  useDeleteApplicationGroupMutation,
  useGetAllApplicationGroupsQuery,
  useUpdateApplicationGroupMutation,
} from 'src/services/switch-rest-api/application-group.service';
import { ALL_SELECTION_ID, isAllSelection } from 'src/utils/selection';

import { useSelectedApplicationGroupId } from './useSelectedApplicationGroupId';

/**
 * Custom hook to get application groups using RTK Query.
 * Provides automatic caching and replaces the old Redux state approach.
 * Also includes CRUD mutation states for create, update, and delete operations.
 *
 * @returns Object containing applicationGroups array, loading states, and mutation states
 */
export const useApplicationGroups = ({
  skip = false,
  syncSelection = true,
  archived,
  applicationGroupId,
}: {
  skip?: boolean;
  syncSelection?: boolean;
  archived?: boolean;
  applicationGroupId?: string;
} = {}) => {
  const { isAuthenticated } = useAuth();
  const { selectedGroupId, setSelectedGroupId } =
    useSelectedApplicationGroupId();

  // Use RTK Query for application groups - provides automatic caching
  const {
    data: applicationGroupsData,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    refetch,
  } = useGetAllApplicationGroupsQuery(
    {
      page: 0,
      limit: 100,
      archived,
      applicationGroupId: isAllSelection(applicationGroupId)
        ? undefined
        : applicationGroupId,
    },
    { skip: !isAuthenticated || skip }
  );

  // Get mutation states (but don't return the mutation functions themselves)
  const [, createMutationState] = useCreateApplicationGroupMutation({
    fixedCacheKey: 'create-application-group',
  });
  const [, updateMutationState] = useUpdateApplicationGroupMutation({
    fixedCacheKey: 'update-application-group',
  });
  const [, deleteMutationState] = useDeleteApplicationGroupMutation({
    fixedCacheKey: 'delete-application-group',
  });

  // Extract application groups from RTK Query response
  const applicationGroups = useMemo(() => {
    if (!applicationGroupsData?.data) return [];
    return applicationGroupsData.data;
  }, [applicationGroupsData]);

  const applicationGroupsCount =
    applicationGroupsData?.totalRecords ??
    applicationGroupsData?.data?.length ??
    0;

  const availableApplicationGroups = useMemo(
    () =>
      applicationGroups.filter(
        (group) => !isAllSelection(group._id) && group.name !== 'All Groups'
      ),
    [applicationGroups]
  );

  useEffect(() => {
    if (!syncSelection || isLoading || isFetching || !isSuccess) return;
    if (availableApplicationGroups.length === 0) return;

    if (availableApplicationGroups.length === 1) {
      const onlyGroupId = availableApplicationGroups[0]._id;

      if (selectedGroupId !== onlyGroupId) {
        setSelectedGroupId(onlyGroupId);
      }

      return;
    }

    const hasSelectedGroup = availableApplicationGroups.some(
      (group) => group._id === selectedGroupId
    );

    if (
      !selectedGroupId ||
      (!isAllSelection(selectedGroupId) && !hasSelectedGroup)
    ) {
      setSelectedGroupId(ALL_SELECTION_ID);
    }
  }, [
    availableApplicationGroups,
    isFetching,
    isLoading,
    isSuccess,
    selectedGroupId,
    setSelectedGroupId,
    syncSelection,
  ]);

  return {
    applicationGroups,
    applicationGroupsCount,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    refetch,
    // Mutation states for CRUD operations
    isCreating: createMutationState.isLoading,
    isUpdating: updateMutationState.isLoading,
    isDeleting: deleteMutationState.isLoading,
    createError: createMutationState.error,
    updateError: updateMutationState.error,
    deleteError: deleteMutationState.error,
  };
};

export default useApplicationGroups;
